import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import DashboardStatsGrid from "../components/DashboardStatsGrid";
import { Table, Image, Input, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import LoadingScreen from "../lib/constants/loadingScreen";
import debounce from "lodash/debounce";
import { MdDelete, MdEditSquare } from "react-icons/md";

const Department = () => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [details, setDetails] = useState([]);
  const [filteredDetails, setFilteredDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const config = {
    headers: {
      Authorization: "Bearer " + localStorage.getItem("adminticket"),
    },
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    setLoading(true);
    axios
      .get(`${apiBaseUrl}/viewDepartment`)
      .then((response) => {
        setDetails(response.data.data);
        setFilteredDetails(response.data.data);
      })
      .catch((error) => {
        console.error("Error fetching department data:", error);
        toast.error(
          "Failed to fetch department data. Please try again later.",
          {
            position: "bottom-right",
          }
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteDepartment = async (department_id) => {
    setActionLoading(true);
    try {
      if (window.confirm("Are you sure want to delete this department?")) {
        const response = await axios.delete(
          `${apiBaseUrl}/department/delete/${department_id}`,
          config
        );
        if (response.data.success) {
          toast.success("Department Deleted", {
            position: "bottom-right",
          });
          window.location.reload();
        } else {
          toast.error("Department Not Deleted", {
            position: "bottom-right",
          });
        }
      }
    } catch (error) {
      console.error("Error deleting department", error);
      toast.error("Failed to delete department! Please try again later.", {
        position: "bottom-right",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const filteredDepartments = useCallback(
    debounce((searchTerm) => {
      if (searchTerm.trim() !== "") {
        const filtered = details.filter((department) =>
          department?.department
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
        setFilteredDetails(filtered);
      } else {
        fetchData();
      }
    }, 500),
    [searchTerm, details]
  );

  const handleSearch = (e) => {
    const searchTerm = e.target.value;
    setSearchTerm(searchTerm);
    filteredDepartments(searchTerm);
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "photo",
      key: "photo",
      render: (photo) => <Image src={photo} width={100} />,
    },
    {
      title: "Department Name",
      dataIndex: "department",
      key: "department",
    },
    {
      title: "Department Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Symptoms",
      dataIndex: "symptom",
      key: "symptom",
      render: (symptom) => (
        <div>
          {symptom.map((item, index) => (
            <span key={index}>
              {item.name}
              {index < symptom.length - 1 && index !== symptom.length - 2
                ? ", "
                : index % 2 === 0 && index !== symptom.length - 1
                ? ", "
                : ""}
              {index % 2 === 1 && index !== symptom.length - 1 && <br />}
            </span>
          ))}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <>
          <Link
            to={`/dashboard/department/update/${record._id}`}
            className="text-indigo-600 hover:text-indigo-900"
          >
            <button className="px-4 py-2 ms-2 rounded-md bg-sky-600 text-white hover:bg-sky-700">
              <MdEditSquare />
            </button>
          </Link>
          <Link
            onClick={() => {
              deleteDepartment(record._id);
            }}
            className="text-indigo-600 hover:text-indigo-900"
          >
            <button className="px-4 py-2 ms-2 rounded-md bg-red-700 text-white hover:bg-red-800">
              <MdDelete />
            </button>
          </Link>
        </>
      ),
    },
  ];

  return (
    <>
      <ToastContainer />

      {actionLoading && <LoadingScreen />}
      <DashboardStatsGrid />

      <div className="container max-w-8xl mx-auto mt-8">
        <div className="mb-4">
          <h1 className="text-3xl font-bold decoration-gray-400">
            Departments
          </h1>
          <div className="flex justify-between mt-2">
            <Input
              placeholder="Search..."
              prefix={<SearchOutlined />}
              allowClear
              value={searchTerm}
              onChange={handleSearch}
              style={{ width: 300 }}
            />
            <div>
              <Link to="/dashboard/department/create">
                <button className="px-4 py-2 rounded-md bg-sky-600 text-white hover:bg-sky-700">
                  Create Department
                </button>
              </Link>
            </div>
          </div>
        </div>
        <div className="flex flex-col">
          <Table
            className="rounded-md shadow-md"
            dataSource={filteredDetails}
            columns={columns}
            rowKey="_id"
            pagination={{
              className: "pe-3",
              defaultPageSize: 15,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            loading={{
              indicator: <Spin size="large" />,
              spinning: loading,
            }}
          />
        </div>
      </div>
    </>
  );
};
export default Department;
